import React from 'react';

import { styled } from '@compiled/react';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { useIntl } from '@atlassian/jira-intl';
import { useBoardSelector } from '../../../../state';
import { getLocale } from '../../../../state/selectors/software/software-selectors';
import { remainingDaysWithTotalDaysSelector } from '../../../../state/selectors/sprint/sprint-selectors';
import { tooltipContentEntryPoint } from './entrypoint';
import messages from './messages';

const RemainingDays = ({
	label,
	onMouseOver,
}: {
	label: string;
	onMouseOver?: React.MouseEventHandler;
}) => (
	<Box
		backgroundColor="elevation.surface.overlay"
		padding="space.100"
		xcss={bottomRightCornerBoxStyles}
		onMouseOver={onMouseOver}
	>
		<Inline space="space.100" alignBlock="center">
			<RecentIconWrapper>
				<EmojiFrequentIcon label={label} />
			</RecentIconWrapper>
			{label}
		</Inline>
	</Box>
);

const entryPointParams = {};
export const SprintRemainingDays = () => {
	const intl = useIntl();
	const { remainingDays, totalDays, startDate, endDate } = useBoardSelector(
		remainingDaysWithTotalDaysSelector,
	);
	const locale = useBoardSelector(getLocale);

	const label = totalDays
		? intl.formatMessage(messages.remainingDays, {
				remainingDays,
				totalDays,
			})
		: intl.formatMessage(messages.remainingDaysNoTotal, { remainingDays });

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		tooltipContentEntryPoint,
		entryPointParams,
	);

	if (remainingDays === null) return null;
	if (startDate === null || endDate === null || locale === null) {
		return <RemainingDays label={label} />;
	}

	return (
		<Tooltip
			position="top"
			content={
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="days-remaining-tooltip"
					packageName="@atlassian/jira-software-board"
					runtimeProps={{
						startDate,
						locale,
						endDate,
					}}
				/>
			}
		>
			<RemainingDays label={label} onMouseOver={entryPointActions.load} />
		</Tooltip>
	);
};

export default SprintRemainingDays;

const bottomRightCornerBoxStyles = xcss({
	fontStyle: 'inherit',
	color: 'color.text.subtlest',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.UNSAFE_small', fontFallback.body.UNSAFE_small),
	fontWeight: token('font.weight.medium', 'medium'),
	boxShadow: 'elevation.shadow.overlay',
	width: '155px',
	height: '50px',
	borderRadius: 'border.radius.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RecentIconWrapper = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N800),
	display: 'flex',
	alignItems: 'center',
	paddingRight: token('space.050', '4px'),
});
